// SteelOps Reports — live KPI snapshots over the company's data. No fancy
// drag-and-drop report builder yet (that's a Pro-tier plugin marked
// `comingSoon` on the plugin registry); these are the read-only summaries an
// owner actually checks on Monday morning.

import { useMemo } from 'react';
import { useApp } from '@/context/AppContext';

const LABOR_RATE_PER_HR = 85;

function fmtMoneyCents(cents) {
  const v = (Number(cents) || 0) / 100;
  return `$${v.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

export default function Reports() {
  const { jobs = [], bids = [], invoices = [], pieceMarks = [], timeCards = [], stats = {} } = useApp();

  const today = new Date().toISOString().slice(0, 10);

  const arSummary = useMemo(() => {
    const sumCents = (list) => list.reduce((a, i) => a + (Number(i.amount) || 0), 0);
    const paid    = invoices.filter(i => i.status === 'paid');
    const open    = invoices.filter(i => i.status === 'sent');
    const overdue = invoices.filter(i => i.status === 'overdue' || (i.status === 'sent' && i.due && i.due < today));
    return {
      paidCents:    sumCents(paid),
      openCents:    sumCents(open),
      overdueCents: sumCents(overdue),
      paidCount:    paid.length,
      openCount:    open.length,
      overdueCount: overdue.length,
    };
  }, [invoices, today]);

  const bidPipeline = useMemo(() => ({
    drafts:    bids.filter(b => b.status === 'draft').length,
    sent:      bids.filter(b => b.status === 'sent').length,
    approved:  bids.filter(b => b.status === 'approved').length,
    declined:  bids.filter(b => b.status === 'declined').length,
    pipelineCents: bids
      .filter(b => b.status === 'sent')
      .reduce((a, b) => a + (Number(b.totalCents) || 0), 0),
  }), [bids]);

  const stageThroughput = useMemo(() => {
    // Counts of piece marks at each stage (1..8) for all active jobs.
    const activeJobIds = new Set(jobs.filter(j => j.status === 'active').map(j => j.id));
    const marks = pieceMarks.filter(p => activeJobIds.has(p.jobId));
    const counts = new Array(8).fill(0);
    marks.forEach(p => {
      const s = Number(p.currentStageId) || 0;
      if (s >= 1 && s <= 8) counts[s - 1] += 1;
    });
    return { counts, total: marks.length };
  }, [jobs, pieceMarks]);

  const laborHours = useMemo(() => {
    const monthAgo = new Date(Date.now() - 30 * 86400000).toISOString();
    const completedPunches = timeCards.filter(tc =>
      tc.punchType === 'clock_out'
      && tc.punchedAt > monthAgo
      && (tc.durationMinutes || 0) > 0
    );
    const totalMinutes = completedPunches.reduce((a, tc) => a + (tc.durationMinutes || 0), 0);
    const totalHours   = totalMinutes / 60;
    return {
      hours:      totalHours,
      laborCost:  totalHours * LABOR_RATE_PER_HR,
      punchCount: completedPunches.length,
    };
  }, [timeCards]);

  return (
    <div className="page fade-up">
      <div className="ph">
        <div>
          <div className="ptitle">Reports</div>
          <div className="psub">Live snapshots — jobs, bids, AR, and labor over the last 30 days.</div>
        </div>
      </div>

      {/* Headline KPIs */}
      <div className="g4">
        <div className="kpi">
          <div className="kpi-bar" style={{ background: 'linear-gradient(90deg,var(--cyan),transparent)' }} />
          <div className="kpi-icon">🏗️</div>
          <div className="kpi-label">Active jobs</div>
          <div className="kpi-value">{stats.activeJobs ?? jobs.filter(j => j.status === 'active').length}</div>
          <div className="kpi-sub">{stats.piecesInProgress ?? 0} pieces in progress</div>
        </div>
        <div className="kpi">
          <div className="kpi-bar" style={{ background: 'linear-gradient(90deg,var(--violet),transparent)' }} />
          <div className="kpi-icon">📋</div>
          <div className="kpi-label">Bid pipeline</div>
          <div className="kpi-value">{bidPipeline.sent}</div>
          <div className="kpi-sub">{fmtMoneyCents(bidPipeline.pipelineCents)} out for review</div>
        </div>
        <div className="kpi">
          <div className="kpi-bar" style={{ background: 'linear-gradient(90deg,var(--rose),transparent)' }} />
          <div className="kpi-icon">⚠️</div>
          <div className="kpi-label">Overdue AR</div>
          <div className="kpi-value">{fmtMoneyCents(arSummary.overdueCents)}</div>
          <div className="kpi-sub">
            {arSummary.overdueCount === 0 ? 'All current' : `${arSummary.overdueCount} invoice${arSummary.overdueCount === 1 ? '' : 's'}`}
          </div>
        </div>
        <div className="kpi">
          <div className="kpi-bar" style={{ background: 'linear-gradient(90deg,var(--amber),transparent)' }} />
          <div className="kpi-icon">⏱️</div>
          <div className="kpi-label">Labor — 30d</div>
          <div className="kpi-value">{Math.round(laborHours.hours)}h</div>
          <div className="kpi-sub">≈ ${Math.round(laborHours.laborCost).toLocaleString()} at ${LABOR_RATE_PER_HR}/hr</div>
        </div>
      </div>

      {/* Stage throughput */}
      <div className="card">
        <div className="card-head">
          <span className="card-title">Piece marks by stage — active jobs</span>
          <span style={{ fontSize: 12, color: 'var(--t3)' }}>{stageThroughput.total} pieces</span>
        </div>
        <div className="card-body">
          {stageThroughput.total === 0 ? (
            <div style={{ padding: 16, textAlign: 'center', color: 'var(--t3)', fontSize: 13 }}>
              No piece marks on active jobs yet.
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 10 }}>
              {['Detail','Material Cut','Fit-Up','Weld','Grind/Clean','Paint','Final QC','Ship'].map((name, idx) => {
                const count = stageThroughput.counts[idx];
                return (
                  <div key={name} style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 22, fontWeight: 800, color: count > 0 ? 'var(--t1)' : 'var(--t4)' }}>{count}</div>
                    <div style={{ fontSize: 10, color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: '.04em', marginTop: 2 }}>{name}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* AR + bid grids */}
      <div className="g2">
        <div className="card">
          <div className="card-head"><span className="card-title">Accounts receivable</span></div>
          <div className="card-body" style={{ display: 'grid', gap: 8, fontSize: 13 }}>
            <Row label={`Paid (${arSummary.paidCount})`}    value={fmtMoneyCents(arSummary.paidCents)} color="var(--teal)" />
            <Row label={`Open (${arSummary.openCount})`}    value={fmtMoneyCents(arSummary.openCents)} color="var(--cyan)" />
            <Row label={`Overdue (${arSummary.overdueCount})`} value={fmtMoneyCents(arSummary.overdueCents)} color="var(--rose)" />
          </div>
        </div>
        <div className="card">
          <div className="card-head"><span className="card-title">Bid pipeline</span></div>
          <div className="card-body" style={{ display: 'grid', gap: 8, fontSize: 13 }}>
            <Row label="Drafts"   value={bidPipeline.drafts} />
            <Row label="Sent — awaiting GC" value={bidPipeline.sent}     color="var(--cyan)" />
            <Row label="Approved" value={bidPipeline.approved} color="var(--teal)" />
            <Row label="Declined" value={bidPipeline.declined} color="var(--rose)" />
          </div>
        </div>
      </div>

      <div style={{ fontSize: 11, color: 'var(--t3)', textAlign: 'center', padding: '8px 0' }}>
        Drag-and-drop custom report builder is on the Pro tier roadmap.
      </div>
    </div>
  );
}

function Row({ label, value, color }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <span style={{ color: 'var(--t3)' }}>{label}</span>
      <span className="mono" style={{ fontWeight: 700, color: color || 'var(--t1)' }}>{value}</span>
    </div>
  );
}
